import { useEffect, useState } from "react";
import {
  useTenantMe,
  useUpdateTenantMe,
  useUploadTenantLogo,
} from "@/lib/tenant-me";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImageUploader } from "@/components/v2/ImageUploader";
import { StatusBadge } from "@/components/v2/StatusBadge";
import { useToast } from "@/components/v2/Toast";
import { SettingsSection } from "@/components/v2/account/SettingsSection";

const DEFAULT_PRIMARY = "#1E3A5F";

/**
 * `/workspace/general` — workspace identity. Name, logo, primary
 * color (drive the public booking page) plus read-only slug, public
 * URL, plan, and status. Currency + timezone live on the sibling
 * `/workspace/regional` page.
 */
export function WorkspaceGeneral() {
  const me = useTenantMe();
  const update = useUpdateTenantMe();
  const uploadLogo = useUploadTenantLogo();
  const toast = useToast();
  const t = me.data?.tenant;

  const [name, setName] = useState("");
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (t) {
      setName(t.name ?? "");
      setPrimaryColor(t.primaryColor ?? DEFAULT_PRIMARY);
    }
  }, [t]);

  const trimmedName = name.trim();
  const dirty =
    trimmedName !== (t?.name ?? "") ||
    primaryColor !== (t?.primaryColor ?? DEFAULT_PRIMARY);
  const valid = trimmedName.length > 0;
  const canSave = dirty && valid && !submitting;

  const onSave = async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      await update.mutateAsync({
        name: trimmedName,
        primaryColor: primaryColor.trim() || undefined,
      });
      toast.success("Workspace updated");
    } catch (e) {
      toast.error("Couldn't save", (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Identity"
        description="How customers see your workspace on the booking page and emails."
      >
        <div className="space-y-5">
          <div className="grid gap-1.5">
            <Label className="text-[13px] font-semibold">Logo</Label>
            <ImageUploader
              shape="square"
              size={80}
              label="Workspace logo"
              value={t?.logoUrl}
              onUpload={(file) => uploadLogo.mutateAsync(file)}
              helpText="Used on the public booking page. PNG / JPG, up to 5 MB."
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="ws-name" className="text-[13px] font-semibold">
                Display name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="ws-name"
                autoComplete="organization"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="ws-slug" className="text-[13px] font-semibold">
                Slug
              </Label>
              <Input
                id="ws-slug"
                value={t?.slug ?? ""}
                readOnly
                className="font-mono text-xs"
              />
            </div>

            <div className="grid gap-1.5 md:col-span-2">
              <Label htmlFor="ws-public" className="text-[13px] font-semibold">
                Public URL
              </Label>
              <Input
                id="ws-public"
                value={`book.booqly.co/${t?.slug ?? ""}`}
                readOnly
                className="font-mono text-xs"
              />
            </div>

            <div className="grid gap-1.5 md:col-span-2">
              <Label htmlFor="ws-color" className="text-[13px] font-semibold">
                Primary color
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="ws-color"
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-10 w-16 p-1 cursor-pointer"
                />
                <Input
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="font-mono text-xs max-w-[160px]"
                />
              </div>
            </div>
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Plan"
        description="Subscription and lifecycle status — managed via Billing."
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-6">
            <ReadOnlyField label="Plan" value={t?.plan ?? "—"} />
            <ReadOnlyField
              label="Created"
              value={
                t?.createdAt
                  ? new Date(t.createdAt).toLocaleDateString()
                  : "—"
              }
            />
          </div>
          {t?.status && <StatusBadge label={t.status} />}
        </div>
      </SettingsSection>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={!canSave}>
          {submitting ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </span>
      <span className="text-sm font-medium capitalize">{value}</span>
    </div>
  );
}
